import { useBalanceQuery } from "@bentolabs/kit/hooks";
import { cn } from "@bentolabs/kit/utils";
import { useChainId } from "wagmi";

import {
  createLatestPricesSelect,
  useLatestPricesQuery,
} from "shared/api/use-latest-price-query";
import { Currency } from "shared/config";
import { AddressLink } from "shared/ui/address-link";
import { Img } from "shared/ui/img";
import { Spinner } from "shared/ui/spinner";
import { mul } from "shared/utils";
import { formatCurrency } from "shared/web3/utils";

type Props<T extends Currency> = {
  className?: string;
  isSelected?: boolean;
  option: T;

  onClick: () => void;
};

export const Option = <T extends Currency>({
  className,
  option,
  isSelected,
  onClick,
}: Props<T>) => {
  const chainId = useChainId();
  const { symbol, address, logoURI, decimals } = option;

  const latestPricesQuery = useLatestPricesQuery({
    select: createLatestPricesSelect(chainId),
  });

  const latestPrice = latestPricesQuery.data?.[symbol];

  const query = useBalanceQuery({ currency: { address, decimals } });

  const usdValue = mul(
    latestPrice?.formatted,
    query.data?.formatted
  )?.toString();

  return (
    <li
      onClick={onClick}
      className={cn(
        "relative flex cursor-pointer items-center gap-x-3.5 px-3 py-2.5 text-xl text-white transition-colors",
        "hover:bg-baltic-sea active:brightness-125",
        isSelected && "bg-baltic-sea",
        className
      )}
    >
      <Img
        src={logoURI}
        className="inline-flex size-9 rounded-full"
        alt={symbol}
      />
      <div className="flex flex-col">
        <span className="inline-flex font-medium text-white">{symbol}</span>
        <AddressLink copyable address={address} className="text-nobel mt-1" />
      </div>
      <div className="ml-auto flex w-24 flex-col md:w-36">
        <span className="overflow-hidden text-right font-medium overflow-ellipsis whitespace-nowrap text-white">
          {query.data?.formatted ?? 0}
        </span>
        {usdValue && (
          <span className="text-nobel mt-1 overflow-hidden text-right text-xs overflow-ellipsis whitespace-nowrap">
            {(latestPricesQuery.isPending || query.isPending) && !usdValue && (
              <Spinner className="size-2" />
            )}

            {usdValue &&
              formatCurrency(usdValue, { decimalScale: 2, prefix: "$" })}
          </span>
        )}
      </div>
    </li>
  );
};
